import { collection, addDoc, updateDoc, deleteDoc, getDocs, doc, query, where } from 'firebase/firestore';
import { orderBy } from 'lodash';

import { DELETE_PROCEDURE, LOAD_DISTRIBUTORS, LOAD_PROCEDURES, SET_DISTRIBUTORS, SET_PROCEDURES } from './actionTypes';

import { DISTRIBUTORS_COLLECTION, PROCEDURES_COLLECTION, TEAMS_COLLECTION } from '../firebase/collections';
import Firebase from '../firebase';
import { companyRoles, countryRoles, distributorRoles } from '../constants/userRoles';
import { userRoles } from '../constants';

const db = Firebase.db;

export const setProcedures = (procedures) => ({ type: SET_PROCEDURES, procedures });

export const filterProceduresByDistributors = (procedures = [], distributors = [], currentUser = {}) => {
  const userDistributorIds = new Set(
    [currentUser.distributor, ...(Array.isArray(currentUser.distributors) ? currentUser.distributors : [])].filter(
      Boolean
    )
  );
  const assignedProcedureIds = new Set(
    distributors
      .filter((distributor) => userDistributorIds.has(distributor.id))
      .flatMap((distributor) => distributor.assignedProcedures || [])
      .map((procedure) => (typeof procedure === 'string' ? procedure : procedure?.id))
      .filter(Boolean)
  );

  return procedures.filter((procedure) => assignedProcedureIds.has(procedure.id));
};

export const getDistributors = () => async (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user.currentUser;

  dispatch({ type: LOAD_DISTRIBUTORS });

  const distributorsCollection = collection(db, DISTRIBUTORS_COLLECTION);

  let q;

  if (companyRoles.includes(currentUser.role)) {
    q = query(
      distributorsCollection,
      where('company', '==', currentUser.company)
    );
  } else {
    q = query(distributorsCollection);
  }

  const snapshot = await getDocs(q);

  let distributors = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  if (countryRoles.includes(currentUser.role)) {
    distributors = distributors.filter((d) => currentUser.administrationCountries?.includes(d.country));
  }

  const sortedDistributors = orderBy(distributors, 'name');
  dispatch({ type: SET_DISTRIBUTORS, distributors: sortedDistributors });

  return sortedDistributors;
};

export const getTeams = () => async () => {
  const db = Firebase.db;

  const teamsCollection = collection(db, TEAMS_COLLECTION);
  const q = query(teamsCollection);

  const snapshot = await getDocs(q);

  let teams = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }));

  return teams;
};

export const fetchProcedures = () => async (dispatch, getState) => {
  const state = getState();
  let distributors = state.distributors?.list || [];
  let teams = state.teams?.list || [];
  const currentUser = state.user.currentUser;

  dispatch({ type: LOAD_PROCEDURES });

  const proceduresCollection = collection(db, PROCEDURES_COLLECTION);

  const snap = await getDocs(proceduresCollection);
  let docs = snap?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }));

  if (distributorRoles.includes(currentUser?.role)) {
    if (!distributors.length) {
      distributors = await dispatch(getDistributors());
    }

    docs = filterProceduresByDistributors(docs, distributors, currentUser);
  }

  if (currentUser?.role === userRoles.ENGINEER.name) {
    if (!teams.length) {
      teams = await dispatch(getTeams());
    }
    const engineeringTeams = teams.filter((t) => t.users?.includes(currentUser.uid));
    const procedures = [];

    if (!engineeringTeams.some((team) => !!team.allProcedures)) {
      engineeringTeams.forEach((team) => {
        team.procedures?.forEach((procedureId) => {
          if (!procedures.includes(procedureId)) {
            procedures.push(procedureId);
          }
        });
      });

      docs = docs.filter((procedure) => procedures.includes(procedure.id));
    }
  }

  return dispatch(setProcedures(orderBy(docs, 'name')));
};

export const createProcedure = (procedure) => (dispatch) => {
  const path = collection(db, PROCEDURES_COLLECTION);
  return addDoc(path, procedure);
};

export const updateProcedure = (procedureId, procedure) => (dispatch) => {
  const path = doc(db, PROCEDURES_COLLECTION, procedureId);
  return updateDoc(path, procedure);
};

export const deleteProcedure = (procedureId) => (dispatch) => {
  const path = doc(db, PROCEDURES_COLLECTION, procedureId);
  return deleteDoc(path,)
    .then((docRef) => dispatch({ type: DELETE_PROCEDURE, id: procedureId }));
}
